/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  RichLogLevel,
  RichLogProductionStep,
  RichLogTextStyle,
} from "../../enums";
import { capitalize } from "../../helpers";
import { RydeAppUnit } from "../common.models";
import { IRichLogRecord } from "./rich-log.models";

export const generateTestRichLogRecords = (count: number): IRichLogRecord[] => {
  const records: IRichLogRecord[] = [];

  const levels = Object.values(RichLogLevel);
  const steps = Object.values(RichLogProductionStep);
  const directions = [
    "Pick Up",
    "Drop Off",
    "Transfer",
    "Relocation",
    "Standby",
  ];
  const entities: RydeAppUnit[] = ["booking", "driver", "vehicle", "passenger"];

  const messageTemplates = [
    "Started processing {{subject}} for {{action}}",
    "Updating status of {{subject}} related to {{action}}",
    "Completed validation for {{subject}} in {{action}} phase",
    "Preparing to dispatch {{subject}} for {{action}} operation",
    "Monitoring {{subject}} during {{action}} stage",
    "Successfully linked {{subject}} to the {{action}} workflow",
    "Initiated background sync for {{subject}} in {{action}} context",
    "Verifying permissions and access for {{subject}} before {{action}}",
    "Scheduled maintenance task involving {{subject}} and {{action}}",
    "Finalizing data integrity checks for {{subject}} in {{action}} mode",
  ];

  for (let i = 0; i < count; i++) {
    const groupId = Math.floor(i / 5) + 1;
    const level = levels[i % levels.length] as RichLogLevel;

    // Set step: ~66% of records have a step, ~33% have undefined
    const step = i % 3 === 0 ? undefined : steps[i % steps.length];

    const direction = directions[i % directions.length];
    const template = messageTemplates[i % messageTemplates.length];
    const entity = entities[i % entities.length];

    const includeLinkItems = i % 2 === 0; // 50% of records
    const includeDirection = true;

    // Build message with placeholders
    const message = template
      .replace("{{subject}}", `{{${entity}s}}`)
      .replace("{{action}}", "{{direction}}");

    // Build linkItems if needed
    const linkItems = includeLinkItems
      ? {
          [`${entity}s`]: {
            entity,
            linkItems: [
              {
                id: 1000 + i * 2,
                name: `${capitalize(entity)} ${1000 + i * 2}`,
              },
              {
                id: 1001 + i * 2,
                name: `${capitalize(entity)} ${1001 + i * 2}`,
              },
            ],
          },
        }
      : undefined;

    // Build textInsertion for direction
    let textStyle: RichLogTextStyle | undefined;

    if (i % 5 === 0) {
      textStyle = RichLogTextStyle.Red;
    } else if (i % 4 === 0) {
      textStyle = RichLogTextStyle.Bold;
    } else if (i % 3 === 0) {
      textStyle = RichLogTextStyle.Italic;
    } // otherwise undefined

    let className: string | undefined;

    if (i % 7 === 0) {
      className = "status-highlight";
    } // otherwise undefined

    const textInsertions = includeDirection
      ? {
          direction: {
            text: direction,
            textStyle,
            className,
          },
        }
      : undefined;

    // Build final record
    const record: IRichLogRecord = {
      timestamp: new Date(Date.now() - (count - i) * 1500).toISOString(),
      level,
      ...(step && { step }), // optional step
      group: groupId,
      message,
      messageParams: {
        ...(linkItems && { linkItems }),
        ...(textInsertions && { textInsertions }),
      },
    };

    records.push(record);
  }

  return records;
};

export const richLogTestRecord: IRichLogRecord = {
  timestamp: new Date().toISOString(),
  level: RichLogLevel.Info,
  group: 1,
  message: "Start producing bookings {{bookings}}  {{direction}}",
  messageParams: {
    linkItems: {
      bookings: {
        entity: "booking",
        linkItems: [
          { id: 200, name: "Booking 2000" },
          { id: 2000, name: "Booking 2000" },
        ],
      },
    },
    textInsertions: {
      direction: {
        text: "Pick Up",
        textStyle: RichLogTextStyle.Bold,
      },
    },
  },
};
